import { useCallback, useEffect, useMemo, useState } from 'react'
import { chargerDetailDossier, contratCourant, type DetailDossier, type DossierPme as Dossier } from '../../api/onboarding'
import {
  etapes, identiteManque, libellePiece, messageErreur, offreManque, piecesManquantes, prochaineAction,
  signatureEnCours, statutDe, LIBELLE_CLE_OFFRE, type PlanPme,
} from '../../api/onboardingRegles'
import { LIBELLE_STATUT_JURIDIQUE, nomPartie, nomSignataire, type StatutJuridique } from '../../api/designationPartie'
import { Icone } from '../Icone'
import { BadgeOnboarding, Bloc, Erreur, Frise, Journal } from './Briques'
import { SectionContrat } from './SectionContrat'
import { SectionDecision } from './SectionDecision'
import { SectionIdentite } from './SectionIdentite'
import { SectionOffre } from './SectionOffre'
import { SectionPieces } from './SectionPieces'

/**
 * Dossier d'un client PME : frise du parcours, prochaine action, puis identité / offre / pièces d'un côté,
 * contrat / décision / journal de l'autre. Les règles de passage sont celles de la base ; ce panneau ne fait
 * que dire POURQUOI un bouton est grisé et QUOI faire ensuite.
 */
export function DossierPme({
  orgId, dossier, plans, grille, peutGerer, proprietaire, surRetour, surMaj,
}: {
  orgId: string
  dossier: Dossier
  plans: PlanPme[]
  grille: { option_longue_distance_mensuel_fcfa: number } | null
  peutGerer: boolean
  proprietaire: boolean
  surRetour: () => void
  /** Recharge la liste des dossiers (statut, identité, offre). */
  surMaj: () => Promise<void>
}) {
  const [detail, setDetail] = useState<DetailDossier | null>(null)
  /** Statut juridique choisi dans le formulaire (undefined = pas encore touché : on suit celui enregistré). */
  const [statutChoisi, setStatutChoisi] = useState<StatutJuridique | null | undefined>(undefined)
  const [contrat, setContrat] = useState<{ id: string; version: number } | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const recharger = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([chargerDetailDossier(dossier.id), contratCourant()])
      setDetail(d)
      setContrat(c)
      setErreur(null)
    } catch (e) {
      setErreur(messageErreur(e))
    }
  }, [dossier.id])

  useEffect(() => { void recharger() }, [recharger])

  const apresChangement = useCallback(async () => {
    await Promise.all([surMaj(), recharger()])
  }, [surMaj, recharger])

  const statut = dossier.onboarding_status
  /* L'offre ENREGISTRÉE, telle que la base la voit : jamais complétée par des valeurs par défaut. */
  const manqueOffre = useMemo(() => offreManque(dossier.offre_choisie, plans), [dossier.offre_choisie, plans])
  const statutJuridique = statutChoisi !== undefined ? statutChoisi : statutDe(dossier)
  const manquePieces = detail ? piecesManquantes(detail.pieces, statutJuridique) : []
  const manqueIdentite = identiteManque(dossier)

  /** Pourquoi l'envoi du contrat est (encore) impossible — dans l'ordre des gardes de la base. */
  const pret: string | null = !detail
    ? 'Chargement du dossier…'
    : manquePieces.length
      ? `Il reste à faire valider : ${manquePieces.map((k) => libellePiece(k, statutJuridique)).join(', ')}.`
      : manqueIdentite.length
        ? `Identité à compléter : ${manqueIdentite.join(', ')}.`
        : manqueOffre.length
          ? `Offre à arrêter : ${manqueOffre.map((k) => LIBELLE_CLE_OFFRE[k] ?? k).join(' ; ')}.`
          : !contrat
            ? 'Aucun contrat publié : le propriétaire doit en publier un dans « Modèles de contrat ».'
            : null

  const action = prochaineAction({
    statut,
    piecesManquantes: manquePieces,
    identiteManque: manqueIdentite,
    offreManque: manqueOffre,
    contratPublie: contrat !== null,
    signatureEnCours: detail ? signatureEnCours(detail.signatures) !== null : false,
    aSignatureSignee: detail ? detail.signatures.some((s) => s.status === 'signe') : false,
    nbPieces: detail?.pieces.length ?? 0,
  })

  const modifiable = peutGerer && statut !== 'refuse' && statut !== 'suspendu'

  return (
    <>
      <button className="btn btn-ghost btn-sm onb-retour" onClick={surRetour}><Icone nom="arrow" classe="onb-retour-ic" /> Retour au pipeline</button>
      <div className="onb-tete">
        <div>
          <h2>{dossier.name}</h2>
          <div className="sous">{[dossier.contact_name, dossier.phone, statutJuridique ? LIBELLE_STATUT_JURIDIQUE[statutJuridique] : null, nomPartie(dossier) && nomPartie(dossier) !== dossier.name ? nomPartie(dossier) : null, statutJuridique === 'societe' && nomSignataire(dossier) ? `Représentant : ${nomSignataire(dossier)}` : null].filter(Boolean).join(' · ') || 'Client PME'}</div>
        </div>
        <span className="sp" />
        <BadgeOnboarding statut={statut} />
      </div>

      <Frise etapes={etapes(statut)} />
      <div className={`onb-action${action.pourStaff ? '' : ' attente'}${statut === 'refuse' || statut === 'suspendu' ? ' alerte' : ''}`}>
        {action.pourStaff ? 'À faire : ' : 'En attente : '}{action.libelle}
      </div>
      <Erreur texte={erreur} />

      <div className="onb-grille">
        <div className="onb-col">
          <SectionIdentite key={`id-${dossier.id}`} dossier={dossier} statut={statut} modifiable={modifiable} surChange={() => void apresChangement()} surStatutJuridique={setStatutChoisi} />
          <SectionOffre key={`of-${dossier.id}`} dossier={dossier} plans={plans} grille={grille} statut={statut} modifiable={modifiable} surChange={() => void apresChangement()} />
          <SectionPieces orgId={orgId} clientId={dossier.id} statut={statutJuridique} pieces={detail?.pieces ?? []} peutGerer={peutGerer} bloque={statut === 'refuse'} surMaj={() => void apresChangement()} />
        </div>
        <div className="onb-col">
          <SectionContrat
            orgId={orgId} dossier={dossier} statut={statut} signatures={detail?.signatures ?? []} contrat={contrat}
            peutGerer={peutGerer} proprietaire={proprietaire} pret={pret} surChange={() => void apresChangement()}
          />
          <SectionDecision clientId={dossier.id} statut={statut} motifActuel={dossier.onboarding_motif} peutGerer={peutGerer} proprietaire={proprietaire} surChange={() => void apresChangement()} />
          <Bloc titre="Journal" droite={detail ? `${detail.journal.length} événement(s)` : undefined}>
            <Journal evenements={detail?.journal ?? []} />
          </Bloc>
        </div>
      </div>
    </>
  )
}
